import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, type AuthState, type Me, type Membership } from '../api/client';
import { useMessagesLive } from './messages';
import { landingPath } from './portal';

interface AuthCtx {
  /** Undefined while the first check is in flight. */
  state: AuthState | undefined;
  /** The API couldn't be reached for the first check. */
  error: string;
  /** Who is signed in (after sign-in, invitation, setup or demo). */
  signedIn: (me: Me) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

/** Who is signed in: asked of the API once, then kept up to date as people sign in and out. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>();
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      setState(await api.authState());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reach Stitch.');
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Any request that comes back "signed out" ends the session here too.
    const out = () => setState((s) => (s ? { ...s, signedIn: false, user: undefined, memberships: undefined } : s));
    window.addEventListener('stitch:signed-out', out);
    return () => window.removeEventListener('stitch:signed-out', out);
  }, [refresh]);

  // Changed through Stitch, here or on another device (your role or access, your own settings): who
  // you are here follows at once. Several changes together are one look.
  const later = useRef<number | undefined>(undefined);
  useMessagesLive(
    (e) => {
      if (e.type !== 'changed' && e.type !== 'reset') return;
      window.clearTimeout(later.current);
      later.current = window.setTimeout(() => void refresh(), 400);
    },
    Boolean(state?.signedIn && state.memberships?.some((m) => m.tenant))
  );

  const signedIn = useCallback(
    (me: Me) => {
      setState((s) => ({ demo: false, setup: false, mail: false, ...s, signedIn: true, user: me.user, memberships: me.memberships }));
      // Stitch staff in the portal of a split Stitch: the link to Stitch Admin comes with the full state.
      if (me.user.staffAccount) void refresh();
    },
    [refresh]
  );

  const signOut = useCallback(async () => {
    await api.signOut().catch(() => undefined);
    setState((s) => (s ? { ...s, signedIn: false, user: undefined, memberships: undefined } : s));
  }, []);

  return <Ctx.Provider value={{ state, error, signedIn, signOut, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}

/** Where someone lands after signing in. */
export function homeFor(me: Pick<AuthState, 'user' | 'memberships'>): string {
  if (me.user?.staff) return '/admin';
  const ms = (me.memberships ?? []).filter((m) => m.tenant);
  if (ms.length === 1) return pathFor(ms[0]!);
  if (ms.length > 1) return '/choose';
  return '/not-ready';
}

/** A company's admin home, an end user's own phone, or Messages for someone with no extension (lib/portal). */
export function pathFor(m: Membership): string {
  return landingPath(m);
}

/** The signed-in person's access to a tenant: staff, admin, user, or none. */
export function accessTo(state: AuthState | undefined, tenant: string): 'staff' | 'admin' | 'user' | null {
  if (!state?.signedIn) return null;
  if (state.user?.staff) return 'staff';
  const m = state.memberships?.find((x) => x.tenant === tenant.toLowerCase());
  return m ? m.role : null;
}
